import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, Card, CardHeader, Button, Typography, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Alert, IconButton, Tooltip, Chip
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon, Key as KeyIcon } from '@mui/icons-material';
import { 
    getPartners, createPartner, deletePartner, 
    regeneratePartnerKey, getPartnerKeyDetails 
} from './api';
import { useAuth } from './AuthContext';

function PartnersPanel({ apiKey, onError }) {
    const { isAdmin } = useAuth();
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newPartnerName, setNewPartnerName] = useState('');
    const [addingPartner, setAddingPartner] = useState(false);
    const [newApiKey, setNewApiKey] = useState('');
    const [openKeyDialog, setOpenKeyDialog] = useState(false);
    
    // Fetch partners
    const fetchPartners = useCallback(async () => {
        if (!apiKey) return;
        
        setLoading(true);
        setError('');
        try {
            const data = await getPartners(apiKey);
            setPartners(data || []);
            if(onError) onError(null);
        } catch (err) {
            console.error("Failed to fetch partners:", err);
            const errorMsg = err.data?.error || err.message || "Failed to fetch partners";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
            if(onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [apiKey, onError]);
    
    useEffect(() => {
        fetchPartners();
    }, [fetchPartners, refreshKey]);
    
    // Handle refresh
    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };
    
    // Handle partner creation
    const handleCreatePartner = async () => {
        if (!newPartnerName.trim()) return;
        
        setAddingPartner(true);
        setError('');
        try {
            const result = await createPartner(apiKey, newPartnerName);
            setNewApiKey(result.apiKey);
            setOpenAddDialog(false);
            setOpenKeyDialog(true);
            setNewPartnerName('');
            handleRefresh();
        } catch (err) {
            console.error("Failed to create partner:", err);
            const errorMsg = err.data?.error || err.message || "Failed to create partner";
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setAddingPartner(false);
        }
    };
    
    // Handle partner deletion
    const handleDeletePartner = async (partnerName) => {
        if (!window.confirm(`Are you sure you want to delete partner "${partnerName}"?`)) return;
        
        setLoading(true);
        setError('');
        try {
            await deletePartner(apiKey, partnerName);
            handleRefresh();
        } catch (err) {
            console.error(`Failed to delete partner ${partnerName}:`, err);
            const errorMsg = err.data?.error || err.message || `Failed to delete partner ${partnerName}`;
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setLoading(false);
        }
    };
    
    // Handle key regeneration
    const handleRegenerateKey = async (partnerName) => {
        if (!window.confirm(`Are you sure you want to regenerate the API key for partner "${partnerName}"? All clients using this key will need to be updated.`)) return;
        
        setLoading(true);
        setError('');
        try {
            const result = await regeneratePartnerKey(apiKey, partnerName);
            setNewApiKey(result.apiKey);
            setOpenKeyDialog(true);
            handleRefresh();
        } catch (err) {
            console.error(`Failed to regenerate key for partner ${partnerName}:`, err);
            const errorMsg = err.data?.error || err.message || `Failed to regenerate key for partner ${partnerName}`;
            setError(`Error: ${errorMsg} (Status: ${err.status || 'N/A'})`);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <Card elevation={2} sx={{ overflow: 'hidden', mt: 3 }}>
            <CardHeader
                title={<Typography variant="h6">Partners</Typography>}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {isAdmin && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenAddDialog(true)}
                                size="small"
                            >
                                Add Partner
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>
                }
            />
            
            <Box sx={{ px: 2, pb: 2 }}>
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                {!loading && !error && (
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Partner Name</TableCell>
                                    <TableCell>Created At</TableCell>
                                    {isAdmin && <TableCell align="right">Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {partners.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 3 : 2} align="center">
                                            <Typography color="textSecondary" sx={{ py: 2 }}>
                                                No partners found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    partners.map((partner) => (
                                        <TableRow key={partner.id}>
                                            <TableCell>{partner.name}</TableCell>
                                            <TableCell>
                                                {partner.createdAt ? new Date(partner.createdAt).toLocaleDateString() : '-'}
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Tooltip title="Regenerate API Key">
                                                            <IconButton 
                                                                size="small" 
                                                                color="primary" 
                                                                onClick={() => handleRegenerateKey(partner.name)}
                                                            >
                                                                <KeyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Partner">
                                                            <IconButton 
                                                                size="small" 
                                                                color="error" 
                                                                onClick={() => handleDeletePartner(partner.name)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
            
            {/* Add Partner Dialog */}
            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
                <DialogTitle>Add New Partner</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Partner Name"
                        fullWidth
                        value={newPartnerName}
                        onChange={(e) => setNewPartnerName(e.target.value)}
                        disabled={addingPartner}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDialog(false)} disabled={addingPartner}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreatePartner} 
                        color="primary" 
                        disabled={!newPartnerName.trim() || addingPartner}
                        variant="contained"
                    >
                        {addingPartner ? <CircularProgress size={24} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* API Key Dialog */}
            <Dialog 
                open={openKeyDialog} 
                onClose={() => setOpenKeyDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>API Key Generated</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Save this API key now! It will not be displayed again.
                    </Alert>
                    <TextField
                        fullWidth
                        value={newApiKey}
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="filled"
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            navigator.clipboard.writeText(newApiKey);
                            alert('API key copied to clipboard');
                        }} 
                        color="primary"
                    >
                        Copy to Clipboard
                    </Button>
                    <Button onClick={() => setOpenKeyDialog(false)} variant="contained" color="primary">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default PartnersPanel;

